import React, { useState } from 'react';
import {
    Box,
    Rating,
    TextField,
    Typography,
    Paper,
    Button,
    Stack,
    Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import theme from '../theme';
import { useLocation, useNavigate } from 'react-router-dom';
import { showNotification } from '../common/components/Notification/ToastifyNotification';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../features/authSlice';
import { sumbitReview } from '../api/sessionApi';
import LoadingSpinner from '../common/components/LoadingSpinner';

const StyledRating = styled(Rating)({
    '& .MuiRating-iconFilled': {
        color: '#faaf00',
    },
    '& .MuiRating-iconHover': {
        color: '#ffbb33',
    },
});

const SessionReview = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const searchParams = new URLSearchParams(location.search);
    const [rating, setRating] = useState(0);
    const [review, setReview] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
        try {
            setSubmitted(true);
            if (rating > 0 && review.trim().length > 0) {
                dispatch(setLoading({
                    loading: true
                }));
                const response = await sumbitReview({
                    token: searchParams.get('token'),
                    rating: rating,
                    feedback: review
                });
                if (response.success) {
                    showNotification(response.message, 'success');
                    setSubmitted(false);
                    navigate('/dashboard');
                    dispatch(setLoading({
                        loading: false
                    }));
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    return (
        <Paper
            elevation={3}
            sx={{
                p: 4,
                maxWidth: 600,
                mx: 'auto',
                borderRadius: 2
            }}
        >
            {loading && (
                <LoadingSpinner />
            )}
            <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
                Write a Session Review
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
                {/* Rating Section */}
                <Box>
                    <Typography component="legend" gutterBottom fontWeight="medium">
                        Your Rating <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <StyledRating
                            name="review-rating"
                            value={rating}
                            precision={0.5}
                            onChange={(event, newValue) => {
                                setRating(newValue);
                                setSubmitted(false); // Reset submitted state when user changes rating
                            }}
                            size="large"
                        />
                    </Box>

                    {/* Show rating error only after submission */}
                    {submitted && rating === 0 && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            Please select a rating
                        </Typography>
                    )}
                </Box>

                {/* Review Text Field */}
                <Box>
                    <Typography component="legend" gutterBottom fontWeight="medium">
                        Your Review <span style={{ color: '#d32f2f' }}>*</span>
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Share your experience... What did you like or dislike?"
                        value={review}
                        onChange={(e) => {
                            setReview(e.target.value);
                            setSubmitted(false); // Reset submitted state when user types
                        }}
                        variant="outlined"
                        required
                        error={submitted && review.trim().length === 0}
                        helperText={submitted && review.trim().length === 0 ? "Please write your review" : ""}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                    border: '1px solid lightgray',
                                },
                                '&.Mui-focused fieldset': {
                                    border: '1px solid lightgray',
                                }
                            },
                        }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            {review.length}/500 characters
                        </Typography>
                    </Box>
                </Box>

                {/* Submit Button - Always enabled */}
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleSubmit}
                    sx={{
                        mt: 2,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.secondary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                        }
                    }}
                >
                    Submit Review
                </Button>
            </Stack>
        </Paper>
    );
};

export default SessionReview;